import React, { useState, Suspense, lazy } from 'react';
import './App.css';
import Icon from './components/Icon';
import { translations, mockCourses, mockStudents, mockTransactions } from './data/mockData';
import ExamCenter from './components/pages/ExamCenter';

// 懶加載頁面組件
const LoginPage = lazy(() => import('./components/pages/LoginPage'));
const UserProgressPage = lazy(() => import('./components/pages/UserProgressPage'));
const UserGradesPage = lazy(() => import('./components/pages/UserGradesPage'));
const UserPurchasesPage = lazy(() => import('./components/pages/UserPurchasesPage'));

export default function App() {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [language, setLanguage] = useState('zh_tw');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [user, setUser] = useState(null);

  const t = translations[language] || translations.zh_tw;



  // 登入處理
  const handleLogin = (userData) => {
    setUser(userData);
    setCurrentPage('dashboard');
  };

  // 登出處理
  const handleLogout = () => {
    setUser(null);
    setCurrentPage('dashboard');
  };

  // 語言切換
  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
  };

  // 如果用戶未登入，顯示登入頁面
  if (!user) {
    return (
      <Suspense fallback={<div className="loading">載入中...</div>}>
        <LoginPage 
          language={language}
          onLogin={handleLogin}
          onLanguageChange={handleLanguageChange}
        />
      </Suspense>
    );
  }

  // 側邊欄菜單項目
  const sidebarItems = [
    { id: 'dashboard', icon: 'home', label: t.dashboard },
    { id: 'myCourses', icon: 'book', label: t.myCourses },
    { id: 'browseCourses', icon: 'search', label: t.browseCourses },
    { id: 'examCenter', icon: 'clipboard', label: t.examCenter },
    { id: 'userProgress', icon: 'chart', label: t.userProgress },
    { id: 'userGrades', icon: 'award', label: t.userGrades },
    { id: 'userPurchases', icon: 'shopping-cart', label: t.userPurchases },
    { id: 'transactions', icon: 'credit-card', label: t.transactions },
    { id: 'profile', icon: 'user', label: t.profile },
    ...(user.role === 'admin' ? [
      { id: 'students', icon: 'users', label: t.students },
      { id: 'analytics', icon: 'bar-chart', label: t.analytics },
      { id: 'settings', icon: 'settings', label: t.settings }
    ] : [])
  ];

  // 語言選項
  const languageOptions = [
    { code: 'zh_tw', name: '繁中', flag: '🇹🇼' },
    { code: 'zh_cn', name: '简中', flag: '🇨🇳' },
    { code: 'en', name: 'EN', flag: '🇺🇸' },
    { code: 'ja', name: '日本', flag: '🇯🇵' }
  ];

  // 渲染頁面內容
  const renderPageContent = () => {
    switch(currentPage) {
      case 'userProgress':
        return (
          <Suspense fallback={<div className="loading">載入中...</div>}>
            <UserProgressPage language={language} />
          </Suspense>
        );
      case 'userGrades':
        return (
          <Suspense fallback={<div className="loading">載入中...</div>}>
            <UserGradesPage language={language} />
          </Suspense>
        );
      case 'userPurchases':
        return (
          <Suspense fallback={<div className="loading">載入中...</div>}>
            <UserPurchasesPage language={language} />
          </Suspense>
        );
      case 'dashboard':
        return <DashboardPage language={language} user={user} />;
      case 'myCourses':
        return <MyCoursesPage language={language} />;
      case 'browseCourses':
        return <BrowseCoursesPage language={language} />;
      case 'examCenter':
        return <ExamCenterPage language={language} />;
      case 'transactions':
        return <TransactionsPage language={language} />;
      case 'profile':
        return <ProfilePage language={language} user={user} />;
      case 'students':
        return <StudentsPage language={language} />;
      case 'analytics':
        return <AnalyticsPage language={language} />;
      case 'settings':
        return <SettingsPage language={language} />;
      case 'personal-info':
        return <PersonalInfoPage language={language} />;
      case 'transaction-history':
        return <TransactionHistoryPage language={language} />;
      case 'student-management':
        return <StudentManagementPage language={language} />;
      case 'data-analytics':
        return <DataAnalyticsPage language={language} />;
      default:
        return <DashboardPage language={language} user={user} />;
    }
  };

  return (
    <div className="app">
      {/* Modern Sidebar */}
      <div className={`sidebar ${sidebarOpen ? '' : 'collapsed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="logo-container">🎓</div>
            <span className="logo-text">ExamOkay</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {/* 學習功能區 */}
          <div className="nav-section">
            <div className="nav-section-label">學習功能</div>
            {sidebarItems.filter(item => ['dashboard', 'myCourses', 'browseCourses', 'examCenter'].includes(item.id)).map(item => (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                className={`nav-item ${currentPage === item.id ? 'active' : ''}`}
              >
                <div className="nav-item-icon">
                  <Icon name={item.icon} />
                </div>
                <span className="nav-item-text">{item.label}</span>
              </button>
            ))}
          </div>

          {/* 個人中心 */}
          <div className="nav-section">
            <div className="nav-section-label">個人中心</div>
            <button
              onClick={() => setCurrentPage('transaction-history')}
              className={`nav-item ${currentPage === 'transaction-history' ? 'active' : ''}`}
            >
              <div className="nav-item-icon">
                <Icon name="credit-card" />
              </div>
              <span className="nav-item-text">交易記錄</span>
            </button>
            <button
              onClick={() => setCurrentPage('personal-info')}
              className={`nav-item ${currentPage === 'personal-info' ? 'active' : ''}`}
            >
              <div className="nav-item-icon">
                <Icon name="user" />
              </div>
              <span className="nav-item-text">個人資料</span>
            </button>
          </div>

          {/* 管理功能 */}
          {user.role === 'admin' && (
            <div className="nav-section">
              <div className="nav-section-label">系統管理</div>
              <button
                onClick={() => setCurrentPage('student-management')}
                className={`nav-item ${currentPage === 'student-management' ? 'active' : ''}`}
              >
                <div className="nav-item-icon">
                  <Icon name="users" />
                </div>
                <span className="nav-item-text">學生管理</span>
              </button>
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{user.avatar}</div>
            <div className="user-details">
              <div className="user-name">{user.name}</div>
              <div className="user-role">{user.level}</div>
            </div>
          </div>
          
          {/* Sidebar Collapse Toggle */}
          <button 
            className="sidebar-collapse-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={sidebarOpen ? "收起選單" : "展開選單"}
          >
            <Icon name={sidebarOpen ? "chevron-left" : "chevron-right"} />
            {sidebarOpen && <span>收起選單</span>}
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`main-content ${sidebarOpen ? '' : 'expanded'}`}>
        {/* Clean Top Navigation */}
        <header className="top-nav">
          <div className="nav-left">
            <button 
              className="sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <Icon name={sidebarOpen ? 'menu' : 'menu'} />
            </button>
            <h1 className="page-title">
              {sidebarItems.find(item => item.id === currentPage)?.label || t.dashboard}
            </h1>
          </div>

          <div className="nav-center">
            <div className="search-container">
              <Icon name="search" className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder={language === 'en' ? 'Search courses, exams...' : 
                           language === 'ja' ? 'コース、試験を検索...' :
                           language === 'zh_cn' ? '搜索课程、考试...' : '搜尋課程、考試...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="nav-right">
            {/* Language Selector */}
            <select 
              className="language-selector"
              value={language}
              onChange={(e) => handleLanguageChange(e.target.value)}
            >
              {languageOptions.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>

            {/* Notifications */}
            <button className="notification-btn">
              <Icon name="bell" />
              <span className="notification-badge"></span>
            </button>

            {/* User Info */}
            <button className="notification-btn" onClick={handleLogout}>
              <Icon name="log-out" />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {renderPageContent()}
        </main>
      </div>
    </div>
  );
}

// 控制台頁面組件
const DashboardPage = ({ language, user }) => {
  const t = translations[language] || translations.zh_tw;
  const [chartPeriod, setChartPeriod] = useState('weekly');



  return (
    <div className="dashboard">
      {/* Welcome Section */}
      <div className="welcome-section">
        <div className="welcome-header">
          <div>
            <h1 className="welcome-title">{t.welcomeBack}, {user.name}! 👋</h1>
            <p className="welcome-subtitle">
              {language === 'en' ? 'Continue your learning journey today' : 
               language === 'ja' ? '今日も学習の旅を続けましょう' :
               language === 'zh_cn' ? '今天继续您的学习之旅' : '今天繼續您的學習之旅'}
            </p>
          </div>
          <div className="welcome-stats">
            <div className="welcome-stat">
              <div className="welcome-stat-value">87%</div>
              <div className="welcome-stat-label">完成率</div>
            </div>
            <div className="welcome-stat">
              <div className="welcome-stat-value">4.8</div>
              <div className="welcome-stat-label">平均分</div>
            </div>
            <div className="welcome-stat">
              <div className="welcome-stat-value">156</div>
              <div className="welcome-stat-label">學習時數</div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Stat Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon blue">📚</div>
            <div className="stat-trend positive">
              ↗ +12%
            </div>
          </div>
          <div className="stat-content">
            <div className="stat-value">8</div>
            <div className="stat-label">進行中課程</div>
          </div>
          <div className="stat-footer">比上月增加 2 門課程</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon green">🎯</div>
            <div className="stat-trend positive">
              ↗ +8%
            </div>
          </div>
          <div className="stat-content">
            <div className="stat-value">15</div>
            <div className="stat-label">已完成考試</div>
          </div>
          <div className="stat-footer">本月新增 3 場考試</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon purple">⏱️</div>
            <div className="stat-trend positive">
              ↗ +25%
            </div>
          </div>
          <div className="stat-content">
            <div className="stat-value">156</div>
            <div className="stat-label">學習時數</div>
          </div>
          <div className="stat-footer">本週學習 28 小時</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon orange">🏆</div>
            <div className="stat-trend positive">
              ↗ +5%
            </div>
          </div>
          <div className="stat-content">
            <div className="stat-value">4.8</div>
            <div className="stat-label">平均成績</div>
          </div>
          <div className="stat-footer">保持優秀表現</div>
        </div>
      </div>

      {/* Dashboard Grid */}
      <div className="dashboard-grid">
        {/* Chart Section */}
        <div className="chart-section">
          <div className="chart-header">
            <h3 className="chart-title">學習進度統計</h3>
            <div className="chart-controls">
              <button 
                className={`chart-btn ${chartPeriod === 'weekly' ? 'active' : ''}`}
                onClick={() => setChartPeriod('weekly')}
              >
                週
              </button>
              <button 
                className={`chart-btn ${chartPeriod === 'monthly' ? 'active' : ''}`}
                onClick={() => setChartPeriod('monthly')}
              >
                月
              </button>
              <button 
                className={`chart-btn ${chartPeriod === 'yearly' ? 'active' : ''}`}
                onClick={() => setChartPeriod('yearly')}
              >
                年
              </button>
            </div>
          </div>
          <div className="chart-placeholder">
            <div className="chart-placeholder-icon">📊</div>
            <div className="chart-placeholder-text">學習進度圖表</div>
            <div className="chart-placeholder-subtitle">
              {chartPeriod === 'weekly' ? '本週' : chartPeriod === 'monthly' ? '本月' : '本年'} 學習數據
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="activity-section">
          <div className="activity-header">
            <h3 className="activity-title">最近活動</h3>
            <p className="activity-subtitle">最新的學習記錄</p>
          </div>
          <div className="activity-list">
            <div className="activity-item">
              <div className="activity-avatar course">📚</div>
              <div className="activity-content">
                <div className="activity-text">完成 React Hooks 課程</div>
                <div className="activity-time">2 小時前</div>
              </div>
            </div>
            <div className="activity-item">
              <div className="activity-avatar exam">🎯</div>
              <div className="activity-content">
                <div className="activity-text">通過中文語文認證考試</div>
                <div className="activity-time">1 天前</div>
              </div>
            </div>
            <div className="activity-item">
              <div className="activity-avatar purchase">💳</div>
              <div className="activity-content">
                <div className="activity-text">購買 Python 機器學習課程</div>
                <div className="activity-time">3 天前</div>
              </div>
            </div>
            <div className="activity-item">
              <div className="activity-avatar achievement">🏆</div>
              <div className="activity-content">
                <div className="activity-text">獲得學習達人徽章</div>
                <div className="activity-time">1 週前</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Data Tables */}
      <div className="data-table-section">
        <div className="table-header">
          <h3 className="table-title">最近考試</h3>
          <p className="table-subtitle">即將到來的考試安排</p>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>考試名稱</th>
              <th>日期</th>
              <th>狀態</th>
              <th>分數</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>React 認證考試</td>
              <td>2025-07-20</td>
              <td><span className="status-badge pending">即將開始</span></td>
              <td>--</td>
              <td><button className="btn btn-sm btn-outline">查看</button></td>
            </tr>
            <tr>
              <td>中文語文認證</td>
              <td>2025-07-15</td>
              <td><span className="status-badge completed">已完成</span></td>
              <td>87分</td>
              <td><button className="btn btn-sm btn-outline">查看</button></td>
            </tr>
            <tr>
              <td>英語口語測試</td>
              <td>2025-07-25</td>
              <td><span className="status-badge active">報名中</span></td>
              <td>--</td>
              <td><button className="btn btn-sm btn-primary">報名</button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="data-table-section">
        <div className="table-header">
          <h3 className="table-title">學習評估</h3>
          <p className="table-subtitle">課程學習進度評估</p>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>課程名稱</th>
              <th>進度</th>
              <th>成績</th>
              <th>狀態</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>React 前端開發</td>
              <td>75%</td>
              <td>A-</td>
              <td><span className="status-badge in-progress">進行中</span></td>
              <td><button className="btn btn-sm btn-primary">繼續學習</button></td>
            </tr>
            <tr>
              <td>中文語文基礎</td>
              <td>100%</td>
              <td>A</td>
              <td><span className="status-badge completed">已完成</span></td>
              <td><button className="btn btn-sm btn-outline">查看證書</button></td>
            </tr>
            <tr>
              <td>日語入門</td>
              <td>85%</td>
              <td>A</td>
              <td><span className="status-badge in-progress">進行中</span></td>
              <td><button className="btn btn-sm btn-primary">繼續學習</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};



// 其他頁面組件保持簡化版本
const MyCoursesPage = ({ language }) => {
  const t = translations[language] || translations.zh_tw;
  
  return (
    <div className="dashboard">
      {/* Page Header */}
      <div className="welcome-section">
        <div className="welcome-header">
          <div>
            <h1 className="welcome-title">{t.myCourses}</h1>
            <p className="welcome-subtitle">管理您已購買的課程和學習進度</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label className="filter-label">課程類別:</label>
            <select className="filter-select">
              <option value="all">全部</option>
              <option value="chinese">中文</option>
              <option value="english">英語</option>
              <option value="programming">程式設計</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">進度狀態:</label>
            <select className="filter-select">
              <option value="all">全部</option>
              <option value="in-progress">進行中</option>
              <option value="completed">已完成</option>
            </select>
          </div>
        </div>
      </div>

      {/* Course Cards */}
      <div className="card-grid">
        {mockCourses.filter(course => course.purchased).map(course => (
          <div key={course.id} className="item-card">
            <div className="item-card-header">
              <div className="item-avatar">{course.image}</div>
              <div className="item-info">
                <div className="item-name">{language === 'en' ? course.nameEn : course.name}</div>
                <div className="item-meta">{course.instructor} • {course.duration}</div>
              </div>
            </div>
            <div className="item-content">
              <p className="item-description">
                {language === 'en' ? course.descriptionEn : course.description}
              </p>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{width: `${course.progress}%`}}
                ></div>
              </div>
              <div className="progress-text">{course.progress}% 完成</div>
              <div className="item-stats">
                <div className="item-stat">
                  <div className="item-stat-value">{course.rating}</div>
                  <div className="item-stat-label">評分</div>
                </div>
                <div className="item-stat">
                  <div className="item-stat-value">{course.students}</div>
                  <div className="item-stat-label">學生</div>
                </div>
              </div>
            </div>
            <div className="item-footer">
              <span className={`status-badge ${course.progress === 100 ? 'completed' : 'in-progress'}`}>
                {course.progress === 100 ? '已完成' : '進行中'}
              </span>
              <button className="btn btn-sm btn-primary">
                {course.progress === 100 ? '查看證書' : '繼續學習'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BrowseCoursesPage = ({ language }) => {
  const t = translations[language] || translations.zh_tw;
  
  return (
    <div className="dashboard">
      {/* Page Header */}
      <div className="welcome-section">
        <div className="welcome-header">
          <div>
            <h1 className="welcome-title">{t.browseCourses}</h1>
            <p className="welcome-subtitle">探索豐富的課程內容，提升您的技能</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label className="filter-label">🔽</label>
            <select className="filter-select">
              <option value="all">全部</option>
              <option value="chinese">中文</option>
              <option value="english">英文</option>
              <option value="mathematics">數學</option>
              <option value="programming">程式設計</option>
            </select>
          </div>
          <div className="filter-group">
            <button className="btn btn-primary">中文</button>
            <button className="btn btn-outline">英文</button>
            <button className="btn btn-outline">數學</button>
            <button className="btn btn-outline">程式設計</button>
          </div>
        </div>
      </div>

      {/* Course Cards */}
      <div className="card-grid">
        {mockCourses.map(course => (
          <div key={course.id} className="item-card">
            <div className="item-card-header">
              <div className="item-avatar">{course.image}</div>
              <div className="item-info">
                <div className="item-name">{language === 'en' ? course.nameEn : course.name}</div>
                <div className="item-meta">{course.instructor}</div>
              </div>
              <span className={`status-badge ${course.purchased ? 'completed' : 'pending'}`}>
                {course.purchased ? '已購買' : '未購買'}
              </span>
            </div>
            <div className="item-content">
              <p className="item-description">
                {language === 'en' ? course.descriptionEn : course.description}
              </p>
              <div className="item-stats">
                <div className="item-stat">
                  <div className="item-stat-value">⭐ {course.rating}</div>
                  <div className="item-stat-label"></div>
                </div>
                <div className="item-stat">
                  <div className="item-stat-value">👥 {course.students}</div>
                  <div className="item-stat-label"></div>
                </div>
                <div className="item-stat">
                  <div className="item-stat-value">🕒 {course.duration}</div>
                  <div className="item-stat-label"></div>
                </div>
              </div>
            </div>
            <div className="item-footer">
              <div className="course-price">
                <span className="current-price">${course.price}</span>
                {course.originalPrice > course.price && (
                  <span className="original-price">${course.originalPrice}</span>
                )}
              </div>
              <button className={`btn btn-sm ${course.purchased ? 'btn-outline' : 'btn-primary'}`}>
                {course.purchased ? '已購買' : 
                 language === 'en' ? 'Add to Cart' : 
                 language === 'ja' ? 'カートに追加' :
                 language === 'zh_cn' ? '加入购物车' : '加入購物車'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ExamCenterPage = ({ language }) => {
  return <ExamCenter language={language} />;
};

const TransactionsPage = ({ language }) => {
  const t = translations[language] || translations.zh_tw;
  
  return (
    <div className="transactions-page">
      <h2>{t.transactions}</h2>
      <div className="transactions-list">
        {mockTransactions.map(transaction => (
          <div key={transaction.id} className="transaction-card">
            <div className="transaction-info">
              <h3>{transaction.item}</h3>
              <p>{transaction.date} {transaction.time}</p>
              <span className="transaction-type">{transaction.type}</span>
            </div>
            <div className="transaction-amount">
              <span className="amount">NT$ {transaction.amount.toLocaleString()}</span>
              <span className={`status ${transaction.status}`}>{transaction.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProfilePage = ({ language, user }) => {
  const t = translations[language] || translations.zh_tw;
  
  return (
    <div className="profile-page">
      <h2>{t.profile}</h2>
      <div className="profile-content">
        <div className="profile-avatar">
          <span className="avatar-large">{user.avatar}</span>
          <h3>{user.name}</h3>
          <p>{user.email}</p>
        </div>
        <div className="profile-details">
          <div className="detail-item">
            <label>{t.name}:</label>
            <span>{user.name}</span>
          </div>
          <div className="detail-item">
            <label>{t.email}:</label>
            <span>{user.email}</span>
          </div>
          <div className="detail-item">
            <label>
              {language === 'en' ? 'Role' : 
               language === 'ja' ? '役割' :
               language === 'zh_cn' ? '角色' : '角色'}:
            </label>
            <span>{user.role}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const StudentsPage = ({ language }) => {
  const t = translations[language] || translations.zh_tw;
  
  return (
    <div className="students-page">
      <h2>{t.students}</h2>
      <div className="students-list">
        {mockStudents.map(student => (
          <div key={student.id} className="student-card">
            <div className="student-avatar">{student.avatar}</div>
            <div className="student-info">
              <h3>{student.name}</h3>
              <p>{student.email}</p>
              <p>{student.course}</p>
              <div className="student-progress">
                <span>Progress: {student.progress}%</span>
                <span>Grade: {student.grade}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AnalyticsPage = ({ language }) => {
  const t = translations[language] || translations.zh_tw;
  
  return (
    <div className="analytics-page">
      <h2>{t.analytics}</h2>
      <div className="analytics-content">
        <p>
          {language === 'en' ? 'Analytics dashboard coming soon...' : 
           language === 'ja' ? 'アナリティクスダッシュボードは近日公開...' :
           language === 'zh_cn' ? '分析仪表板即将推出...' : '分析儀表板即將推出...'}
        </p>
      </div>
    </div>
  );
};

const SettingsPage = ({ language }) => {
  const t = translations[language] || translations.zh_tw;
  
  return (
    <div className="settings-page">
      <h2>{t.settings}</h2>
      <div className="settings-content">
        <p>
          {language === 'en' ? 'System settings coming soon...' : 
           language === 'ja' ? 'システム設定は近日公開...' :
           language === 'zh_cn' ? '系统设置即将推出...' : '系統設置即將推出...'}
        </p>
      </div>
    </div>
  );
};

// Personal Info Page Component
const PersonalInfoPage = ({ language }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: 'Zhang Student',
    email: 'zhang@example.com',
    phone: '0XXX XXX XXX',
    birthDate: '2003-01-15',
    gender: '男性'
  });

  const interests = ['日語學習', '英語學習', '程式設計'];

  const accountSettings = [
    { id: 1, icon: '🔒', title: '修改密碼', description: '更改您的帳戶密碼' },
    { id: 2, icon: '🛡️', title: '隱私設定', description: '管理您的隱私偏好' },
    { id: 3, icon: '🔔', title: '通知設定', description: '設定通知偏好' },
    { id: 4, icon: '💳', title: '付款方式', description: '管理付款方式' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    setIsEditing(false);
    // Here you would typically save to backend
  };

  return (
    <div className="dashboard">
      {/* Page Header */}
      <div className="welcome-section">
        <div className="welcome-header">
          <div>
            <h1 className="welcome-title">個人資料</h1>
            <p className="welcome-subtitle">管理您的個人資訊和帳戶設定</p>
          </div>
        </div>
      </div>

      {/* Personal Info Form */}
      <div className="personal-info-section">
        <div className="info-card">
          <div className="card-header">
            <h3>基本資料</h3>
            <button 
              className={`btn ${isEditing ? 'btn-primary' : 'btn-outline'}`}
              onClick={isEditing ? handleSave : () => setIsEditing(true)}
            >
              {isEditing ? '儲存資料' : '編輯資料'}
            </button>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">姓名</label>
              {isEditing ? (
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              ) : (
                <div className="form-value">{formData.name}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">電子信箱</label>
              {isEditing ? (
                <input
                  type="email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              ) : (
                <div className="form-value">{formData.email}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">聯絡電話</label>
              {isEditing ? (
                <input
                  type="tel"
                  className="form-input"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              ) : (
                <div className="form-value">{formData.phone}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">出生日期</label>
              {isEditing ? (
                <input
                  type="date"
                  className="form-input"
                  value={formData.birthDate}
                  onChange={(e) => handleInputChange('birthDate', e.target.value)}
                />
              ) : (
                <div className="form-value">{formData.birthDate}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">性別</label>
              {isEditing ? (
                <select
                  className="form-input"
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                >
                  <option value="男性">男性</option>
                  <option value="女性">女性</option>
                  <option value="其他">其他</option>
                </select>
              ) : (
                <div className="form-value">{formData.gender}</div>
              )}
            </div>
          </div>
        </div>

        {/* Interests Section */}
        <div className="info-card">
          <div className="card-header">
            <h3>興趣標籤</h3>
          </div>
          <div className="interests-grid">
            {interests.map((interest, index) => (
              <span key={index} className="interest-tag">
                {interest}
              </span>
            ))}
          </div>
        </div>

        {/* Account Settings */}
        <div className="info-card">
          <div className="card-header">
            <h3>帳戶設定</h3>
          </div>
          <div className="settings-list">
            {accountSettings.map(setting => (
              <div key={setting.id} className="setting-item">
                <div className="setting-icon">{setting.icon}</div>
                <div className="setting-details">
                  <div className="setting-title">{setting.title}</div>
                  <div className="setting-description">{setting.description}</div>
                </div>
                <button className="btn btn-sm btn-outline">設定</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Transaction History Page Component
const TransactionHistoryPage = ({ language }) => {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [filterType] = useState('all');

  // Mock transaction data matching the screenshot structure
  const transactions = [
    {
      id: '001',
      date: '2025-06-20',
      type: 'course',
      category: '課程購買',
      itemName: 'React 前端開發',
      paymentMethod: '信用卡',
      amount: 799,
      status: 'completed'
    },
    {
      id: '002',
      date: '2025-06-18',
      type: 'exam',
      category: '考試報名',
      itemName: '雅思模擬考試',
      paymentMethod: 'PayPal',
      amount: 200,
      status: 'completed'
    },
    {
      id: '003',
      date: '2025-06-15',
      type: 'course',
      category: '課程購買',
      itemName: 'JavaScript 全端開發',
      paymentMethod: '信用卡',
      amount: 999,
      status: 'completed'
    },
    {
      id: '004',
      date: '2025-06-12',
      type: 'exam',
      category: '考試報名',
      itemName: '英語會話評估',
      paymentMethod: '銀行轉帳',
      amount: 180,
      status: 'completed'
    },
    {
      id: '005',
      date: '2025-06-10',
      type: 'course',
      category: '課程購買',
      itemName: '英語會話實戰',
      paymentMethod: '信用卡',
      amount: 599,
      status: 'completed'
    },
    {
      id: '006',
      date: '2025-06-08',
      type: 'course',
      category: '課程購買',
      itemName: '微積分入門',
      paymentMethod: 'PayPal',
      amount: 499,
      status: 'completed'
    },
    {
      id: '007',
      date: '2025-06-05',
      type: 'exam',
      category: '考試報名',
      itemName: '全端開發技能考試',
      paymentMethod: '信用卡',
      amount: 350,
      status: 'completed'
    },
    {
      id: '008',
      date: '2025-06-01',
      type: 'course',
      category: '課程購買',
      itemName: '統計學基礎',
      paymentMethod: '銀行轉帳',
      amount: 399,
      status: 'completed'
    },
    {
      id: '009',
      date: '2025-05-28',
      type: 'course',
      category: '課程購買',
      itemName: '中文語文基礎',
      paymentMethod: '信用卡',
      amount: 299,
      status: 'completed'
    },
    {
      id: '010',
      date: '2025-05-25',
      type: 'course',
      category: '課程購買',
      itemName: '商務英語溝通',
      paymentMethod: 'PayPal',
      amount: 699,
      status: 'completed'
    }
  ];

  const filteredTransactions = transactions.filter(transaction => {
    if (filterType === 'all') return true;
    return transaction.type === filterType;
  });

  return (
    <div className="dashboard">
      {/* Page Header */}
      <div className="welcome-section">
        <div className="welcome-header">
          <div>
            <h1 className="welcome-title">交易記錄</h1>
            <p className="welcome-subtitle">查看您的所有購買和付款記錄</p>
          </div>
        </div>
      </div>

      {/* Transaction Table */}
      <div className="data-table-section">
        <div className="table-header">
          <h3 className="table-title">所有交易</h3>
          <div className="table-actions">
            <button className="btn btn-outline">匯出記錄</button>
            <button className="btn btn-primary">篩選</button>
          </div>
        </div>

        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>日期</th>
                <th>類型</th>
                <th>項目</th>
                <th>支付方式</th>
                <th>金額</th>
                <th>狀態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map(transaction => (
                <tr key={transaction.id}>
                  <td>{transaction.date}</td>
                  <td>
                    <span className={`category-badge ${transaction.type}`}>
                      {transaction.category}
                    </span>
                  </td>
                  <td>{transaction.itemName}</td>
                  <td>{transaction.paymentMethod}</td>
                  <td>${transaction.amount}</td>
                  <td>
                    <span className="status-badge completed">已完成</span>
                  </td>
                  <td>
                    <button 
                      className="btn btn-sm btn-link"
                      onClick={() => setSelectedTransaction(transaction)}
                    >
                      查看詳情
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <div className="modal-overlay" onClick={() => setSelectedTransaction(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>交易詳情</h2>
              <button 
                className="close-btn"
                onClick={() => setSelectedTransaction(null)}
              >
                <Icon name="x" />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="transaction-detail-info">
                <div className="detail-row">
                  <span className="detail-label">交易編號:</span>
                  <span className="detail-value">TXN{selectedTransaction.id.padStart(8, '0')}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">交易時間:</span>
                  <span className="detail-value">{selectedTransaction.date}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">項目名稱:</span>
                  <span className="detail-value">{selectedTransaction.itemName}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">交易金額:</span>
                  <span className="detail-value">${selectedTransaction.amount}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">付款方式:</span>
                  <span className="detail-value">{selectedTransaction.paymentMethod}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">交易狀態:</span>
                  <span className="detail-value">已完成</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Student Management Page Component
const StudentManagementPage = ({ language }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');

  // Mock student data matching the screenshot
  const students = [
    {
      id: '001',
      name: '張小明',
      email: 'zhang@example.com',
      course: 'React 前端開發',
      progress: 75,
      grade: 'A',
      status: 'active'
    },
    {
      id: '002',
      name: '李美華',
      email: 'li@example.com',
      course: '英語會話實戰',
      progress: 60,
      grade: 'B+',
      status: 'active'
    },
    {
      id: '003',
      name: '王志強',
      email: 'wang@example.com',
      course: '微積分入門',
      progress: 90,
      grade: 'A+',
      status: 'active'
    },
    {
      id: '004',
      name: '陳雅婷',
      email: 'chen@example.com',
      course: 'Python 數據分析',
      progress: 45,
      grade: 'B',
      status: 'warning'
    },
    {
      id: '005',
      name: '林建宏',
      email: 'lin@example.com',
      course: '日語基礎課程',
      progress: 85,
      grade: 'A',
      status: 'active'
    },
    {
      id: '006',
      name: '黃詩涵',
      email: 'huang@example.com',
      course: '商務英語溝通',
      progress: 70,
      grade: 'B+',
      status: 'active'
    },
    {
      id: '007',
      name: '劉志明',
      email: 'liu@example.com',
      course: '中文語文基礎',
      progress: 55,
      grade: 'B',
      status: 'inactive'
    }
  ];

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.course.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalStudents = students.length;
  const activeStudents = students.filter(s => s.status === 'active').length;
  const warningStudents = students.filter(s => s.status === 'warning').length;
  const averageProgress = Math.round(students.reduce((sum, s) => sum + s.progress, 0) / students.length);

  return (
    <div className="dashboard">
      {/* Page Header */}
      <div className="welcome-section">
        <div className="welcome-header">
          <div>
            <h1 className="welcome-title">學生管理</h1>
            <p className="welcome-subtitle">管理學生註冊和學習進度資訊</p>
          </div>
          <button className="btn btn-primary">
            + 新增學生
          </button>
        </div>
      </div>

      {/* Student Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-number">{totalStudents}</div>
            <div className="stat-label">學生總數</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-number">{activeStudents}</div>
            <div className="stat-label">活躍學生</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <div className="stat-number">{warningStudents}</div>
            <div className="stat-label">待關注</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-number">{averageProgress}%</div>
            <div className="stat-label">平均進度</div>
          </div>
        </div>
      </div>

      {/* Student List */}
      <div className="data-table-section">
        <div className="table-header">
          <h3 className="table-title">學生列表</h3>
          <div className="table-actions">
            <div className="search-box">
              <input
                type="text"
                placeholder="搜尋學生..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <select 
              className="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">依姓名排序</option>
              <option value="progress">依進度排序</option>
              <option value="grade">依成績排序</option>
            </select>
          </div>
        </div>

        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>學生</th>
                <th>課程</th>
                <th>進度</th>
                <th>成績</th>
                <th>狀態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map(student => (
                <tr key={student.id}>
                  <td>
                    <div className="student-info">
                      <div className="student-avatar">
                        {student.name.charAt(0)}
                      </div>
                      <div>
                        <div className="student-name">{student.name}</div>
                        <div className="student-email">{student.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>{student.course}</td>
                  <td>
                    <div className="progress-container">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${student.progress}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">{student.progress}%</span>
                    </div>
                  </td>
                  <td>
                    <span className={`grade-badge ${student.grade.replace('+', 'plus')}`}>
                      {student.grade}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${student.status}`}>
                      {student.status === 'active' && '活躍'}
                      {student.status === 'warning' && '待關注'}
                      {student.status === 'inactive' && '非活躍'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn btn-sm btn-link">👁️</button>
                      <button className="btn btn-sm btn-link">✏️</button>
                      <button className="btn btn-sm btn-link">🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Data Analytics Page Component
const DataAnalyticsPage = ({ language }) => {
  return (
    <div className="dashboard">
      {/* Page Header */}
      <div className="welcome-section">
        <div className="welcome-header">
          <div>
            <h1 className="welcome-title">數據分析</h1>
            <p className="welcome-subtitle">深入了解平台使用情況和學習成效</p>
          </div>
        </div>
      </div>

      {/* Analytics Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">📊</div>
          <div className="stat-content">
            <div className="stat-value">1,248</div>
            <div className="stat-label">總用戶數</div>
            <div className="stat-change positive">+12% 本月</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">📈</div>
          <div className="stat-content">
            <div className="stat-value">89%</div>
            <div className="stat-label">課程完成率</div>
            <div className="stat-change positive">+5% 本月</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">⏱️</div>
          <div className="stat-content">
            <div className="stat-value">2.5小時</div>
            <div className="stat-label">平均學習時間</div>
            <div className="stat-change positive">+8% 本月</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon orange">💰</div>
          <div className="stat-content">
            <div className="stat-value">NT$ 125,600</div>
            <div className="stat-label">月收入</div>
            <div className="stat-change positive">+15% 本月</div>
          </div>
        </div>
      </div>

      {/* Analytics Charts */}
      <div className="analytics-charts">
        <div className="chart-section">
          <div className="chart-header">
            <h3>用戶增長趨勢</h3>
          </div>
          <div className="chart-placeholder">
            <div className="chart-mock">
              📈 圖表區域 - 顯示過去6個月的用戶增長趨勢
            </div>
          </div>
        </div>

        <div className="chart-section">
          <div className="chart-header">
            <h3>課程受歡迎程度</h3>
          </div>
          <div className="chart-placeholder">
            <div className="chart-mock">
              📊 圖表區域 - 顯示各課程的註冊人數和完成率
            </div>
          </div>
        </div>

        <div className="chart-section">
          <div className="chart-header">
            <h3>學習時間分布</h3>
          </div>
          <div className="chart-placeholder">
            <div className="chart-mock">
              🕐 圖表區域 - 顯示用戶一天中的學習時間分布
            </div>
          </div>
        </div>

        <div className="chart-section">
          <div className="chart-header">
            <h3>收入分析</h3>
          </div>
          <div className="chart-placeholder">
            <div className="chart-mock">
              💹 圖表區域 - 顯示月收入和課程銷售情況
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 